// API base URL for constructing full photo URLs
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Helper to get full URL for profile photos
const getFullPhotoUrl = (relativeUrl) => {
  if (!relativeUrl) return null;
  if (relativeUrl.startsWith('http')) return relativeUrl;
  return `${API_BASE_URL}${relativeUrl}`;
};

// Transform API data to BalkanTree format - handles actual API structure
export const processBalkanTreeData = (apiResponse) => {

  if (!apiResponse?.family_members && !apiResponse?.parent_nodes) {
    console.warn('No family_members or parent_nodes array found in API response');
    return [];
  }

  // Process all family members to create a comprehensive list
  const balkanNodes = [];
  const processedIds = new Set();

  // First pass: Process all family members from the family_members array (if it exists)
  if (apiResponse.family_members && Array.isArray(apiResponse.family_members)) {
    apiResponse.family_members.forEach(member => {
      if (processedIds.has(member.id)) return;

      // Try multiple name field possibilities
      const personName = member.name || 
                        member.fullName || 
                        member.firstName || 
                        (member.firstName && member.lastName ? `${member.firstName} ${member.lastName}` : '') ||
                        member.data?.full_name ||
                        member.data?.name ||
                        member.data?.first_name ||
                        `Person ${member.id}`;

      const personNode = {
        id: member.id,
        name: personName,
        gender: member.gender === 'unknown' ? 'male' : member.gender, // BalkanTree needs 'male'/'female'
        generation: apiResponse.generation_map?.[member.id] || 0,

        // Initialize relationship fields
        mid: null,  // Mother ID
        fid: null,  // Father ID  
        pids: [],   // Partner/Spouse IDs

        // BalkanTree display fields at root level
        photo: getFullPhotoUrl(member.data?.photo_url) || getDefaultPhoto(member.gender),
        born: formatBirthDateWithLabel(member.data?.date_of_birth)
      };

      // Add additional data fields
      personNode.data = {
        email: member.data?.email || '',
        phone: member.data?.phone || '',
        date_of_birth: member.data?.date_of_birth,
        full_name: member.data?.full_name || member.name,
        status: member.data?.status || 'active',
        city: member.data?.city_of_birth || '',
        country: member.data?.country_of_birth || '',
        living: member.data?.living !== undefined ? member.data.living : true
      };

      balkanNodes.push(personNode);
      processedIds.add(member.id);
    });
  }

  // Also process parent_nodes if they exist and aren't already in family_members
  if (apiResponse.parent_nodes && Array.isArray(apiResponse.parent_nodes)) {
    
    apiResponse.parent_nodes.forEach(parent => {
      if (processedIds.has(parent.id)) return;

      // Try multiple name field possibilities for parent
      const parentName = parent.name || 
                        parent.fullName || 
                        parent.firstName || 
                        (parent.firstName && parent.lastName ? `${parent.firstName} ${parent.lastName}` : '') ||
                        parent.data?.full_name ||
                        parent.data?.name ||
                        parent.data?.first_name ||
                        `Person ${parent.id}`;

      const parentNode = {
        id: parent.id,
        name: parentName,
        gender: parent.gender === 'unknown' ? 'male' : parent.gender,
        generation: apiResponse.generation_map?.[parent.id] || -1, // Parents typically at generation -1

        mid: null,
        fid: null,
        pids: [],

        // BalkanTree display fields at root level
        photo: getFullPhotoUrl(parent.data?.photo_url) || getDefaultPhoto(parent.gender),
        born: formatBirthDateWithLabel(parent.data?.date_of_birth)
      };

      // Add additional data fields for parent
      parentNode.data = {
        email: parent.data?.email || '',
        phone: parent.data?.phone || '',
        date_of_birth: parent.data?.date_of_birth,
        full_name: parent.data?.full_name || parent.name,
        status: parent.data?.status || 'active',
        city: parent.data?.city_of_birth || '',
        country: parent.data?.country_of_birth || '',
        living: parent.data?.living !== undefined ? parent.data.living : true
      };

      balkanNodes.push(parentNode);
      processedIds.add(parent.id);
    });
  }

  // Second pass: Set relationships based on the actual data structure
  balkanNodes.forEach(node => {
    // Find the node data from either family_members or parent_nodes
    const member = apiResponse.family_members?.find(fm => fm.id === node.id) ||
                   apiResponse.parent_nodes?.find(pn => pn.id === node.id);
    
    if (!member) return;

    // Set parent relationships using parent_nodes
    if (apiResponse.parent_nodes && Array.isArray(apiResponse.parent_nodes)) {
      apiResponse.parent_nodes.forEach(parent => {
        const relType = parent.data?.relationship_type?.toLowerCase() || '';
        
        if (relType.includes('mother')) {
          node.mid = parent.id;
        } else if (relType.includes('father')) {
          node.fid = parent.id;
        }
      });
    }

    // Set spouse relationships
    if (member.data?.spouse_of) {
      node.pids = [member.data.spouse_of];
    }

    // Also check if any parent nodes have spouse relationships
    if (apiResponse.parent_nodes) {
      apiResponse.parent_nodes.forEach(parent => {
        if (parent.data?.spouse_of) {
          const spouseNode = balkanNodes.find(n => n.id === parent.id);
          if (spouseNode && !spouseNode.pids.includes(parent.data.spouse_of)) {
            spouseNode.pids.push(parent.data.spouse_of);
          }
        }
      });
    }
  });

  // Debug: Show complete node structure for BalkanTree
  
  return balkanNodes;
};

// Simple transform function for the new simplified API format
export const transformToBalkanFormatWithGenerations = (apiData) => {
  
  if (!apiData) {
    console.warn('No API data provided');
    return [];
  }

  // NEW: Direct nodes array format - perfect for BalkanTree!
  if (apiData.nodes && Array.isArray(apiData.nodes)) {

    // Add default photo, format dates, normalize gender for BalkanTree
    let balkanNodes = apiData.nodes.map(node => ({
      ...node,
      // BalkanTree only supports 'male' and 'female'; default 'unknown' to 'male'
      gender: node.gender === 'unknown' ? 'male' : (node.gender || 'male'),
      photo: getFullPhotoUrl(node.photo) || getFullPhotoUrl(node.data?.photo_url) || getDefaultPhoto(node.gender),
      born: node.born ? formatBirthDateWithLabel(node.born) : 'DOB: ',
      // Ensure required fields exist
      pids: node.pids || [],
      mid: node.mid || null,
      fid: node.fid || null
    }));

    // Sort nodes so the oldest generation comes first.
    // BalkanTree picks its visual root from the first root node in the array;
    // if a younger-generation root (e.g. a spouse) appears first, the library
    // may never render the older generation above them.
    const nodeIds = new Set(balkanNodes.map(n => n.id));
    const depth = {};
    const computeDepth = (id, visited) => {
      if (depth[id] !== undefined) return depth[id];
      if (visited.has(id)) return 0;
      visited.add(id);
      const children = balkanNodes.filter(n => n.mid === id || n.fid === id);
      if (children.length === 0) { depth[id] = 0; return 0; }
      const maxChild = Math.max(...children.map(c => computeDepth(c.id, visited)));
      depth[id] = maxChild + 1;
      return depth[id];
    };
    balkanNodes.forEach(n => computeDepth(n.id, new Set()));

    balkanNodes.sort((a, b) => (depth[b.id] || 0) - (depth[a.id] || 0));

    // BALKAN WORKAROUND: The library does not render a spouse's parents.
    // When both spouses have parents, the "secondary" spouse's parents are dropped.
    // Fix: Move the in-law parent links from the spouse to the CHILDREN.
    // Instead of: Joseph+Pat → Kim → (spouse of Nitin) → Max
    // We make:    Joseph+Pat are fid/mid of a VIRTUAL child node that is
    //             a partner of the real Kim, connecting both families.
    //
    // Simpler: Just swap fid/mid on children so that the spouse's father
    // becomes the child's fid (grandfather), and the primary parent's father
    // stays as-is via a virtual intermediate. This is too complex.
    //
    // SIMPLEST WORKING FIX: Strip fid/mid from the spouse node (Kim) and
    // instead make a phantom "Kim (parents)" node that is a child of Joseph+Pat
    // and a partner of the real Kim. The phantom node has tags:['inlaw-link'].
    const nodeMap = {};
    balkanNodes.forEach(n => { nodeMap[n.id] = n; });

    const phantomNodes = [];
    const processedSpouses = new Set();

    balkanNodes.forEach(node => {
      if (!node.pids || node.pids.length === 0) return;
      node.pids.forEach(spouseId => {
        if (processedSpouses.has(spouseId)) return;
        const spouse = nodeMap[spouseId];
        if (!spouse) return;

        const nodeHasParents = !!(node.fid || node.mid);
        const spouseHasParents = !!(spouse.fid || spouse.mid);

        if (nodeHasParents && spouseHasParents) {
          // Both have parents — the library will skip one side.
          // Determine which side gets skipped (the one whose node is the "spouse" in pids).
          // We'll fix the spouse's parents by creating phantom nodes.
          processedSpouses.add(node.id);
          processedSpouses.add(spouseId);

          // The spouse whose parents would be skipped — try to detect which one.
          // The library renders fid-chain of the first node it processes.
          // We'll assume the spouse (the one referenced in pids) gets skipped.
          const skippedParent = spouse; // Kim in our example

          // Save and remove Kim's parent links
          const savedFid = skippedParent.fid;
          const savedMid = skippedParent.mid;
          skippedParent.fid = null;
          skippedParent.mid = null;

          // Create a phantom node that represents Kim's family origin
          // This phantom is a child of Joseph+Pat and connects to Kim via pids
          const phantomId = `_origin_${skippedParent.id}`;
          phantomNodes.push({
            id: phantomId,
            name: '',
            gender: skippedParent.gender || 'female',
            fid: savedFid,
            mid: savedMid,
            pids: [skippedParent.id],
            photo: '',
            born: '',
            tags: ['inlaw-connector'],
          });
          // Add the phantom to Kim's pids so they're linked
          skippedParent.pids = [...(skippedParent.pids || []), phantomId];
        }
      });
    });

    if (phantomNodes.length > 0) {
      balkanNodes.unshift(...phantomNodes);
    }

    return balkanNodes;
  }

  // LEGACY: Use family_members array for complete person data (fallback)
  if (apiData.family_members && Array.isArray(apiData.family_members)) {
    console.warn('⚠️ Using legacy family_members format - consider upgrading API to nodes format');
    
    // Extract generation mapping if available
    const generationMap = apiData.generation_map || {};
    
    // 🔍 DEBUG - Log all family members before processing
    apiData.family_members.forEach((member, index) => {
    });
    
    // 🔍 DEBUG - Specifically look for Sonali
    const sonaliInArray = apiData.family_members.find(m => m.name?.includes('Sonali') || m.id === '1afb8b12-8bfa-4535-a60f-b78e5ebceb7f');
    
    const balkanNodes = apiData.family_members.map(member => {
      
      // 🔧 COMPLETE FIX: Establish proper family hierarchy
      // Based on the API, here's the correct structure:
      // Generation -1: Sangeeta (mother) & Chandrashekhar (father) - grandparents  
      // Generation 0: Nitin (son), Kimberly (Nitin's spouse), Anil (son-in-law)
      // Generation 1: Sonali (daughter, married to Anil)
      
      let generation;
      const relationshipType = member.data?.relationship_type?.toLowerCase() || '';
      const hasParents = member.data?.parents?.length > 0;
      
      // Establish hierarchy based on relationships, not API generation_map
      if (relationshipType === 'mother' || relationshipType === 'father' || 
          (relationshipType === 'parent' && !hasParents)) {
        // Sangeeta & Chandrashekhar are the oldest generation (grandparents)
        generation = -1;
      } else if (hasParents || relationshipType === 'son' || 
                 (member.name === 'Nitin Somalwar')) {
        // Nitin is the middle generation (has parents)
        generation = 0;
      } else if (relationshipType === 'daughter' || relationshipType === 'son-in-law') {
        // Sonali & Anil are same generation (married to each other)  
        generation = 0;
      } else if (relationshipType === 'spouse') {
        // Spouses should be same generation as their partner
        const spouseId = member.data?.spouse_of;
        if (spouseId) {
          const spouse = apiData.family_members.find(fm => fm.id === spouseId);
          if (spouse?.data?.parents?.length > 0) {
            generation = 0; // Spouse of someone with parents
          } else {
            generation = -1; // Spouse of grandparent generation
          }
        } else {
          generation = 0; // Default
        }
      } else {
        // Default fallback
        generation = 0;
      }
      
      // 🔧 Convert negative generations for BalkanTree compatibility
      const balkanGeneration = generation + 1; // -1→0, 0→1, 1→2
      generation = balkanGeneration;
      
      // Set parent relationships using parents array from API
      let mid = null;
      let fid = null;
      
      if (member.data?.parents && Array.isArray(member.data.parents)) {
        
        // Find mother from parents array - enhanced logic
        mid = member.data.parents.find(parentId => {
          const parent = apiData.family_members.find(fm => fm.id === parentId);
          if (!parent) return false;
          
          const relType = parent?.data?.relationship_type?.toLowerCase() || '';
          const gender = parent?.data?.gender || parent?.gender;
          
          // Priority order for mother detection:
          // 1. Explicit "mother" relationship
          // 2. Female gender
          // 3. For Sangeeta specifically (she's the mother)
          const ismother = relType.includes('mother') || 
                          gender === 'female' ||
                          parent.name?.includes('Sangeeta');
          
          return ismother;
        }) || null;
        
        // Find father from parents array - enhanced logic
        fid = member.data.parents.find(parentId => {
          if (parentId === mid) return false; // Skip if this is already the mother
          
          const parent = apiData.family_members.find(fm => fm.id === parentId);
          if (!parent) return false;
          
          const relType = parent?.data?.relationship_type?.toLowerCase() || '';
          const gender = parent?.data?.gender || parent?.gender;
          
          // Priority order for father detection:
          // 1. Explicit "father" relationship
          // 2. Male gender
          // 3. For Chandrashekhar specifically (he's the father)
          // 4. "parent" relationship (if not already mother)
          // 5. Default: assume father if not mother (for 2-parent families)
          const isfather = relType.includes('father') ||
                          gender === 'male' ||
                          parent.name?.includes('Chandrashekhar') ||
                          (relType.includes('parent') && !relType.includes('mother')) ||
                          (member.data.parents.length === 2 && parentId !== mid);
          
          return isfather;
        }) || null;
        
      }
      
      // Set spouse relationships using spouse_of field (check both locations)
      const pids = [];
      const spouseOf = member.data?.spouse_of || member.spouse_of;
      if (spouseOf) {
        pids.push(spouseOf);
      }
      
      return {
        id: member.id,
        name: member.name || `Person ${member.id}`,
        gender: member.gender || 'unknown',
        generation: generation,
        
        // Parent relationships
        mid: mid,
        fid: fid,
        
        // Spouse relationships  
        pids: pids,
        
        // Additional fields
        photo: getFullPhotoUrl(member.data?.photo_url) || getDefaultPhoto(member.gender),
        born: formatBirthDateWithLabel(member.data?.date_of_birth),
        email: member.data?.email || '',
        phone: member.data?.phone || '',
        city: member.data?.city_of_birth || '',
        country: member.data?.country_of_birth || '',
        living: member.data?.living !== undefined ? member.data.living : true,
        
        // Required fields for Balkan FamilyTree
        stpid: null,
        facts: member.facts || [],
        names: member.names || [],
        
        // Store spouse_of for the bidirectional fixing
        spouse_of: spouseOf,
        data: member.data
      };
    });
    
    // Fix bidirectional spouse relationships (O(n) instead of O(n²))
    const spouseMap = new Map(); // spouse_id -> [person_ids_that_reference_them]
    
    // First pass: build spouse mapping
    balkanNodes.forEach(node => {
      const spouseOf = node.data?.spouse_of || node.spouse_of;
      if (spouseOf) {
        if (!spouseMap.has(spouseOf)) {
          spouseMap.set(spouseOf, []);
        }
        spouseMap.get(spouseOf).push(node.id);
      }
    });
    
    // Second pass: create bidirectional relationships
    balkanNodes.forEach(node => {
      if (spouseMap.has(node.id)) {
        const spouses = spouseMap.get(node.id);
        spouses.forEach(spouseId => {
          if (!node.pids.includes(spouseId)) {
            node.pids.push(spouseId);
            const spouseNode = balkanNodes.find(n => n.id === spouseId);
          }
        });
      }
    });

    // Apply additional spouse relationship detection for same generation/opposite gender
    const generation0Members = balkanNodes.filter(node => node.generation === 0);
    generation0Members.forEach(member => {
      // Find potential spouses (same generation, opposite gender, not already in pids)
      const potentialSpouses = generation0Members.filter(other =>
        other.id !== member.id &&
        other.gender !== member.gender &&
        !member.pids.includes(other.id)
      );

      // Add potential spouses to pids if found
      potentialSpouses.forEach(spouse => {
        if (!member.pids.includes(spouse.id)) {
          member.pids.push(spouse.id);
        }
        // Make bidirectional
        if (!spouse.pids.includes(member.id)) {
          spouse.pids.push(member.id);
        }
      });
    });
    
    // Add children from the children array if they're not already in family_members
    if (apiData.children && Array.isArray(apiData.children)) {
      
      apiData.children.forEach(child => {
        const exists = balkanNodes.find(node => node.id === child.id);
        if (!exists) {
          
          // Children of root person should be generation 1 (converted to gen 2 for BalkanTree)
          const childNode = {
            id: child.id,
            name: child.name || `Person ${child.id}`,
            gender: child.data?.gender || child.gender || 'unknown',
            generation: 2, // Children of root person
            
            // Parent relationships - connect to root person as father
            mid: null, 
            fid: apiData.id || null,
            
            // Spouse relationships
            pids: [],
            
            // Additional fields
            photo: getFullPhotoUrl(child.data?.photo_url) || getDefaultPhoto(child.data?.gender || child.gender),
            born: formatBirthDateWithLabel(child.data?.date_of_birth),
            living: child.data?.living !== undefined ? child.data.living : true,
            names: child.names || [],
            
            data: child.data
          };
          
          balkanNodes.push(childNode);
        }
      });
      
    }
    
    // Log generation distribution for debugging
    const generationStats = {};
    balkanNodes.forEach(node => {
      const gen = node.generation;
      generationStats[gen] = (generationStats[gen] || 0) + 1;
    });
    
    // Log relationship mapping for debugging
    const relationshipStats = {
      withParents: balkanNodes.filter(n => n.mid || n.fid).length,
      withSpouses: balkanNodes.filter(n => n.pids.length > 0).length,
      total: balkanNodes.length
    };
    
    // Add any parent_nodes that aren't already included
    if (apiData.parent_nodes && Array.isArray(apiData.parent_nodes)) {
      
      apiData.parent_nodes.forEach(parent => {
        // Check if this parent is already in balkanNodes
        const exists = balkanNodes.find(node => node.id === parent.id);
        if (!exists) {
          
          const parentNode = {
            id: parent.id,
            name: parent.name || `Person ${parent.id}`,
            gender: parent.gender || 'unknown',
            generation: generationMap[parent.id] || -1,
            
            // Parent relationships (parents don't have parents in this context)
            mid: null,
            fid: null,
            
            // Spouse relationships
            pids: parent.data?.spouse_of ? [parent.data.spouse_of] : [],
            
            // Additional fields
            photo: getFullPhotoUrl(parent.data?.photo_url) || getDefaultPhoto(parent.gender),
            born: formatBirthDateWithLabel(parent.data?.date_of_birth),
            living: parent.data?.living !== undefined ? parent.data.living : true,
            names: parent.names || []
          };
          
          balkanNodes.push(parentNode);
        }
      });
      
    }
    
    // 🔍 FINAL DEBUG - What are we actually returning?
    balkanNodes.forEach((node, index) => {
    });
    
    // 🔍 Check generation distribution
    const finalGenStats = {};
    balkanNodes.forEach(node => {
      const gen = node.generation;
      finalGenStats[gen] = (finalGenStats[gen] || 0) + 1;
    });
    
    // 🔍 Check for potential BalkanTree filtering issues
    const negativeGenNodes = balkanNodes.filter(n => n.generation < 0);
    if (negativeGenNodes.length > 0) {
      console.warn('⚠️ WARNING: BalkanTree may not display negative generation nodes:', negativeGenNodes.map(n => `${n.name} (gen ${n.generation})`));
    }
    
    // 🔍 Check for orphaned nodes (no relationships)
    const orphanedNodes = balkanNodes.filter(n => !n.mid && !n.fid && n.pids.length === 0);
    if (orphanedNodes.length > 1) { // More than just root person
      console.warn('⚠️ WARNING: Multiple orphaned nodes found (may not connect properly in BalkanTree):', orphanedNodes.map(n => `${n.name} (gen ${n.generation})`));
    }
    
    // 🔍 Check one more time for Anil Patel
    const anilFinal = balkanNodes.find(n => n.name?.includes('Anil Patel'));
    
    // 🔍 Check one more time for Sonali
    const sonaliFinal = balkanNodes.find(n => n.name?.includes('Sonali') || n.id === '1afb8b12-8bfa-4535-a60f-b78e5ebceb7f');
    
    return balkanNodes;
  }
  
  // Fallback to previous logic if family_members array is not available
  
  // Extract generation mapping for legacy support
  const generationMap = apiData.generation_map || {};
  
  // Process the tree data recursively (legacy support)
  const processNode = (node, defaultGeneration = 0) => {
    if (!node || !node.id) {
      console.warn('Invalid node data:', node);
      return null;
    }

    // Use generation from generation_map, fallback to defaultGeneration
    const generation = generationMap[node.id] !== undefined ? generationMap[node.id] : defaultGeneration;
    
    // Extract name from various possible fields
    let personName = node.name || 
                    node.fullName || 
                    node.data?.full_name ||
                    node.data?.name ||
                    `${node.firstName || node.data?.first_name || ''} ${node.lastName || node.data?.last_name || ''}`.trim();
    
    if (!personName || personName.trim() === '') {
      personName = `Person ${node.id}`;
    }

    const balkanNode = {
      id: node.id,
      name: personName,
      gender: node.gender || node.data?.gender || 'unknown',
      generation: generation,

      // The API now properly sets these relationships
      mid: node.mid || null,  // Mother ID
      fid: node.fid || null,  // Father ID  
      pids: node.pids || [],  // Partner/Spouse IDs

      // Additional fields
      photo: getFullPhotoUrl(node.data?.photo_url) || getDefaultPhoto(node.data?.gender || node.gender),
      born: formatBirthDateWithLabel(node.data?.date_of_birth),
      email: node.data?.email || '',
      phone: node.data?.phone || '',
      city: node.data?.city_of_birth || '',
      country: node.data?.country_of_birth || '',
      living: node.data?.living !== undefined ? node.data.living : true,
      
      // Required fields for Balkan FamilyTree
      stpid: null,
      facts: node.facts || [],
      names: node.names || []
    };

    return balkanNode;
  };

  const nodes = [];

  // If API data has the legacy structure with generation_map
  if (apiData.generation_map && Object.keys(generationMap).length > 0) {
    
    // Process main node if it exists
    if (apiData.id) {
      const mainNode = processNode(apiData);
      if (mainNode) nodes.push(mainNode);
    }
    
    // Process children if they exist
    if (apiData.children && Array.isArray(apiData.children)) {
      apiData.children.forEach(child => {
        const childNode = processNode(child);
        if (childNode) nodes.push(childNode);
      });
    }
    
    // If we have additional nodes in the response, process them too
    if (apiData.nodes && Array.isArray(apiData.nodes)) {
      apiData.nodes.forEach(node => {
        const balkanNode = processNode(node);
        if (balkanNode) nodes.push(balkanNode);
      });
    }
  } else {
    // Fallback to legacy processing if generation_map is not available
    return transformToBalkanFormat(apiData);
  }

  return nodes;
};

// Legacy transform function - kept for backward compatibility
export const transformToBalkanFormat = (familyTreeData) => {
  
  // Handle the new hierarchical API response structure with root person and children
  if (familyTreeData && familyTreeData.id && familyTreeData.children && Array.isArray(familyTreeData.children)) {
    familyTreeData.children.forEach((child, index) => {
    });
    
    const balkanNodes = [];
    const processedIds = new Set();
    
    // Process root person
    const rootPerson = familyTreeData;
    
    // Debug: Log all name-related fields
    
    // Try multiple name field combinations
    let personName = rootPerson.name || 
                    rootPerson.fullName || 
                    rootPerson.data?.full_name ||
                    rootPerson.data?.name ||
                    `${rootPerson.firstName || rootPerson.data?.first_name || ''} ${rootPerson.lastName || rootPerson.data?.last_name || ''}`.trim();
    
    // If still empty, fallback to Person + ID
    if (!personName || personName.trim() === '') {
      personName = `Person ${rootPerson.id}`;
    }
    
    const rootNode = {
      id: rootPerson.id,
      name: personName,
      gender: rootPerson.gender || rootPerson.data?.gender || 'unknown',
      photo: getFullPhotoUrl(rootPerson.data?.photo_url) || getDefaultPhoto(rootPerson.data?.gender),
      born: formatBirthDateWithLabel(rootPerson.data?.date_of_birth),
      email: rootPerson.data?.email || '',
      phone: rootPerson.data?.phone || '',
      city: rootPerson.data?.city_of_birth || '',
      country: rootPerson.data?.country_of_birth || '',
      living: rootPerson.data?.living !== undefined ? rootPerson.data.living : true,
      // Required fields for Balkan FamilyTree
      pids: [],
      mid: null,
      fid: null,
      stpid: null,
      generation: 0,
      facts: [],
      names: []
    };
    
    balkanNodes.push(rootNode);
    processedIds.add(rootPerson.id);
    
    // Process children
    rootPerson.children.forEach((child, index) => {
      if (!processedIds.has(child.id)) {
        // Note: Complex spouse detection logic removed - API now handles this properly
        // The API provides correct relationship data including generation levels
        let mid = null;
        let fid = null;
        let generation = 1; // Default to children generation - API will override with generation_map

        const childNode = {
          id: child.id,
          name: child.name || child.fullName || `${child.firstName || child.data?.first_name || ''} ${child.lastName || child.data?.last_name || ''}`.trim(),
          gender: child.gender || child.data?.gender || 'unknown',
          photo: getFullPhotoUrl(child.data?.photo_url) || getDefaultPhoto(child.data?.gender),
          born: formatBirthDateWithLabel(child.data?.date_of_birth),
          email: child.data?.email || '',
          phone: child.data?.phone || '',
          city: child.data?.city_of_birth || '',
          country: child.data?.country_of_birth || '',
          living: child.data?.living !== undefined ? child.data.living : true,
          // Required fields for Balkan FamilyTree
          pids: [],
          mid: mid,
          fid: fid,
          stpid: null,
          generation: generation,
          facts: [],
          names: []
        };
        
        balkanNodes.push(childNode);
        processedIds.add(child.id);
        
        // Note: Relationship assignment now handled by API generation_map
        // Complex spouse/child detection logic removed as API provides correct data
      }
    });
    
    balkanNodes.forEach(node => {
    });
    return balkanNodes;
  }
  
  // Handle the current API response structure - direct person object
  if (familyTreeData && familyTreeData.id && familyTreeData.first_name) {
    const person = familyTreeData;
    
    // Debug: Log the name fields and date fields
    
    const fullName = `${person.first_name || ''} ${person.last_name || ''}`.trim();
    
    // Try to find a valid birth date from multiple possible fields
    const birthDate = person.date_of_birth;
    
    return [{
      id: person.id,
      name: fullName || `Person ${person.id}`,
      gender: person.gender || 'unknown',
      photo: getFullPhotoUrl(person.photo_url) || getDefaultPhoto(person.gender),
      born: formatBirthDateWithLabel(birthDate),
      email: person.email || '',
      phone: person.phone || '',
      city: person.city_of_birth || '',
      country: person.country_of_birth || '',
      living: person.living,
      // Required fields for Balkan FamilyTree
      pids: [],
      mid: null,
      fid: null,
      stpid: null,
      generation: 0,
      facts: person.facts || [],
      names: person.names || []
    }];
  }
  
  // Handle the actual API response structure
  if (familyTreeData && familyTreeData.person) {
    const person = familyTreeData.person;
    
    // Debug: Log the name fields and date fields
    
    const fullName = `${person.first_name || ''} ${person.last_name || ''}`.trim();
    
    // Try to find a valid birth date from multiple possible fields
    const birthDate = person.birth_fact || person.date_of_birth || person.birth_date || person.born;
    
    return [{
      id: person.id,
      name: fullName || person.primary_name || `Person ${person.id}`,
      gender: person.gender || 'unknown',
      photo: getFullPhotoUrl(person.photo_url) || getDefaultPhoto(person.gender),
      born: formatBirthDateWithLabel(birthDate),
      email: person.email || '',
      phone: person.phone || '',
      city: person.city || '',
      country: person.country || '',
      living: person.living,
      // Required fields for Balkan FamilyTree
      pids: [],
      mid: null,
      fid: null,
      stpid: null,
      generation: 0,
      facts: person.facts || [],
      names: person.names || []
    }];
  }
  
  // Handle legacy structure with generations (if it exists)
  if (familyTreeData && familyTreeData.generations) {
    return processGenerationsStructure(familyTreeData);
  }
  
  // Fallback: Create a basic tree structure from minimal data
  if (familyTreeData && familyTreeData.id) {
    const person = familyTreeData;
    
    // Use name or fullName if available, otherwise construct from first/last names
    const fullName = person.name || person.fullName || 
                    `${person.first_name || ''} ${person.last_name || ''}`.trim() || 
                    `Person ${person.id}`;
    const birthDate = person.date_of_birth;
    
    return [{
      id: person.id,
      name: fullName,
      gender: person.gender || 'unknown',
      photo: getDefaultPhoto(person.gender),
      born: formatBirthDateWithLabel(birthDate),
      email: person.email || '',
      phone: person.phone || '',
      city: person.city_of_birth || '',
      country: person.country_of_birth || '',
      living: person.living !== undefined ? person.living : true,
      // Required fields for Balkan FamilyTree
      pids: [],
      mid: null,
      fid: null,
      stpid: null,
      generation: 0,
      facts: [],
      names: []
    }];
  }
  
  console.warn('No valid family tree data structure found:', familyTreeData);
  return [];
};

// Process the legacy generations structure
function processGenerationsStructure(familyTreeData) {
  const balkanNodes = [];
  const processedIds = new Set();

  // Process each generation
  familyTreeData.generations.forEach((generation) => {
    if (!generation || !Array.isArray(generation.persons)) {
      console.warn('Invalid generation data:', generation);
      return;
    }
    
    generation.persons.forEach((person) => {
      if (!person || !person.id) {
        console.warn('Invalid person data:', person);
        return;
      }
      
      if (processedIds.has(person.id)) return;
      
      // Try to find a valid birth date from multiple possible fields
      const birthDate = person.birth_fact || person.date_of_birth || person.birth_date || person.born;
      
      const balkanNode = {
        id: person.id,
        name: `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.primary_name || `Person ${person.id}`,
        gender: person.gender || 'unknown',
        photo: getFullPhotoUrl(person.photo_url) || getDefaultPhoto(person.gender),
        born: formatBirthDateWithLabel(birthDate),
        email: person.email || '',
        phone: person.phone || '',
        city: person.city || '',
        country: person.country || '',
        living: person.living,
        // Required fields for Balkan FamilyTree - must be properly initialized
        pids: [], // Partner IDs (will be populated from relationships)
        mid: null, // Mother ID
        fid: null, // Father ID
        // Additional required fields to prevent undefined errors
        stpid: null, // Step parent ID
        // Custom fields
        generation: generation.level,
        facts: person.facts || [],
        names: person.names || []
      };

      balkanNodes.push(balkanNode);
      processedIds.add(person.id);
    });
  });

  // Process relationships to set family connections
  if (familyTreeData.relationships && Array.isArray(familyTreeData.relationships)) {
    familyTreeData.relationships.forEach((relationship) => {
      if (!relationship || !relationship.type || !relationship.person1 || !relationship.person2) {
        console.warn('Invalid relationship data:', relationship);
        return;
      }
      
      const { type, person1, person2 } = relationship;
      
      if (type === 'parent-child') {
        const childNode = balkanNodes.find(node => node.id === person2.id);
        const parentNode = balkanNodes.find(node => node.id === person1.id);
        
        if (childNode && parentNode) {
          if (parentNode.gender === 'female') {
            childNode.mid = parentNode.id;
          } else if (parentNode.gender === 'male') {
            childNode.fid = parentNode.id;
          }
        }
      } else if (type === 'couple') {
        const person1Node = balkanNodes.find(node => node.id === person1.id);
        const person2Node = balkanNodes.find(node => node.id === person2.id);
        
        if (person1Node && person2Node) {
          // Ensure pids arrays are initialized
          if (!Array.isArray(person1Node.pids)) person1Node.pids = [];
          if (!Array.isArray(person2Node.pids)) person2Node.pids = [];
          
          if (!person1Node.pids.includes(person2.id)) {
            person1Node.pids.push(person2.id);
          }
          if (!person2Node.pids.includes(person1.id)) {
            person2Node.pids.push(person1.id);
          }
        }
      }
    });
  }

  // Final validation - ensure all required fields are present
  const validatedNodes = balkanNodes.map(node => ({
    ...node,
    // Ensure all required fields are properly initialized
    pids: Array.isArray(node.pids) ? node.pids : [],
    mid: node.mid || null,
    fid: node.fid || null,
    stpid: node.stpid || null,
    // Ensure other required fields
    gender: node.gender || 'unknown',
    living: typeof node.living === 'boolean' ? node.living : true
  }));
  
  return validatedNodes;
}

// Transform person data to Balkan format for individual nodes
export const transformPersonToBalkan = (person) => {
  // Try to find a valid birth date from multiple possible fields
  const birthDate = person.birth_fact || person.date_of_birth || person.birth_date || person.born;
  
  return {
    id: person.id,
    name: `${person.first_name || ''} ${person.last_name || ''}`.trim() || person.primary_name || `Person ${person.id}`,
    gender: person.gender || 'unknown',
    photo: getFullPhotoUrl(person.photo_url) || getDefaultPhoto(person.gender),
    born: birthDate ? formatDate(birthDate) : '',
    email: person.email || '',
    phone: person.phone || '',
    city: person.city || '',
    country: person.country || '',
    living: person.living,
    // Required fields for Balkan FamilyTree - must be properly initialized
    pids: [], // Partner IDs
    mid: null, // Father ID
    fid: null, // Mother ID
    stpid: null, // Step parent ID
    // Additional fields
    facts: person.facts || [],
    names: person.names || []
  };
};

// Get default photo based on gender
export const getDefaultPhoto = (gender) => {
  // Return null - no default photo, let Balkan tree show its default or initials
  return null;
};

// Format date for display
export const formatDate = (dateString) => {
  if (!dateString || dateString === null || dateString === undefined) return '';

  try {
    let date;

    // Parse date string WITHOUT timezone interpretation
    // "2010-07-29" should be July 29, not shifted by timezone
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      // ISO date format (YYYY-MM-DD) - parse as local date
      const parts = dateString.split('-');
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // JS months are 0-indexed
      const day = parseInt(parts[2], 10);
      date = new Date(year, month, day);
    } else {
      // For other formats, use default parsing
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) {
      console.warn('Invalid date string:', dateString);
      return '';
    }

    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

    return formattedDate;
  } catch (error) {
    console.error('Error formatting date:', error, 'Input:', dateString);
    return '';
  }
};

// Helper function to format birth date with DOB label
export const formatBirthDateWithLabel = (dateValue) => {
  if (!dateValue || dateValue === null || dateValue === undefined) {
    return 'DOB: ';
  }
  const formatted = formatDate(dateValue);
  return formatted ? `DOB: ${formatted}` : 'DOB: ';
};

// Calculate age from date of birth
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;

  try {
    const today = new Date();
    let birthDate;

    // Parse date string WITHOUT timezone interpretation
    if (typeof dateOfBirth === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)) {
      const parts = dateOfBirth.split('-');
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      birthDate = new Date(year, month, day);
    } else {
      birthDate = new Date(dateOfBirth);
    }

    if (isNaN(birthDate.getTime())) return null;

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  } catch (error) {
    console.error('Error calculating age:', error);
    return null;
  }
};

// Get relationship type between two people
export const getRelationshipType = (person1, person2, relationships) => {
  if (!relationships || !person1 || !person2) return 'unknown';
  
  const relationship = relationships.find(rel => 
    (rel.person1.id === person1.id && rel.person2.id === person2.id) ||
    (rel.person1.id === person2.id && rel.person2.id === person1.id)
  );
  
  return relationship ? relationship.type : 'unknown';
};

// Build family tree metadata
export const buildTreeMetadata = (familyTreeData) => {
  if (!familyTreeData) return null;
  
  // Handle the new hierarchical API response structure with root person and children
  if (familyTreeData.id && familyTreeData.children && Array.isArray(familyTreeData.children)) {
    const totalPersons = 1 + familyTreeData.children.length; // Root + children
    const totalGenerations = 2; // Root generation + children generation
    
    return {
      totalPersons,
      totalGenerations,
      lastUpdated: new Date().toISOString(),
      rootPerson: {
        id: familyTreeData.id,
        first_name: familyTreeData.data?.first_name,
        last_name: familyTreeData.data?.last_name,
        primary_name: familyTreeData.name
      }
    };
  }
  
  // Handle the actual API response structure
  if (familyTreeData.person) {
    return {
      totalPersons: 1,
      totalGenerations: 1,
      lastUpdated: new Date().toISOString(),
      rootPerson: familyTreeData.person
    };
  }
  
  // Handle legacy structure with generations
  if (familyTreeData.generations) {
    const totalPersons = familyTreeData.generations.reduce((total, gen) => total + gen.persons.length, 0);
    const totalGenerations = familyTreeData.generations.length;
    
    return {
      totalPersons,
      totalGenerations,
      lastUpdated: familyTreeData.metadata?.last_updated || new Date().toISOString(),
      rootPerson: familyTreeData.root_person
    };
  }
  
  return {
    totalPersons: 0,
    totalGenerations: 0,
    lastUpdated: new Date().toISOString(),
    rootPerson: null
  };
};

// Filter persons by search term
export const filterPersons = (persons, searchTerm) => {
  if (!searchTerm) return persons;
  
  const term = searchTerm.toLowerCase();
  return persons.filter(person => 
    person.first_name?.toLowerCase().includes(term) ||
    person.last_name?.toLowerCase().includes(term) ||
    `${person.first_name} ${person.last_name}`.toLowerCase().includes(term)
  );
};

// Sort persons by generation and birth date
export const sortPersonsByGeneration = (persons, generations) => {
  if (!generations || !Array.isArray(generations)) return persons;
  
  const sortedPersons = [];
  
  generations.forEach((generation) => {
    const generationPersons = generation.persons.sort((a, b) => {
      if (!a.date_of_birth && !b.date_of_birth) return 0;
      if (!a.date_of_birth) return 1;
      if (!b.date_of_birth) return -1;
      
      return new Date(a.date_of_birth) - new Date(b.date_of_birth);
    });
    
    sortedPersons.push(...generationPersons);
  });
  
  return sortedPersons;
};

// Enhanced relationship type display for Unknown relationships
export const enhanceRelationshipTypes = (familyMembers, generationMap) => {
  return familyMembers.map(member => {
    const generation = generationMap[member.id];

    // Enhanced relationship type logic
    if (member.data?.relationship_type === "Unknown") {
      if (generation === 0 && member.data?.spouse_of) {
        member.data.relationship_type = "Spouse";
      } else if (generation === 0 && member.gender === "male") {
        member.data.relationship_type = "Spouse"; // Likely spouse
      } else if (generation === 1) {
        member.data.relationship_type = "Child";
      } else if (generation === -1) {
        member.data.relationship_type = "Parent";
      }
    }

    return member;
  });
};

// Spouse relationship detection for BalkanTree pids
export const detectSpouseRelationships = (familyMembers, generationMap) => {
  return familyMembers.map(member => {
    const memberGeneration = generationMap[member.id];

    // Find potential spouses (same generation, opposite gender)
    const potentialSpouses = familyMembers.filter(other =>
      other.id !== member.id &&
      generationMap[other.id] === memberGeneration &&
      other.gender !== member.gender &&
      memberGeneration === 0 // Focus on root generation
    );

    // Set pids (partner IDs) for BalkanTree
    if (potentialSpouses.length > 0) {
      member.pids = potentialSpouses.map(spouse => spouse.id);
    }

    return member;
  });
};
